// Mock data for Depreciation module
import type { Project, DepreciationPeriod, DepreciationBatch, DepreciationItem } from '@/types/depreciation';

export const mockProjects: Project[] = [
  {
    id: 'proj-001',
    name: 'IT Equipment Standard',
    description: 'Standard depreciation for IT assets',
    method: 'Straight Line',
    usefulLife: 4,
    residualPolicy: 'value',
    residualValue: 100,
    proRataRule: 'Mid-Month',
    startTrigger: 'In-Service Date',
    capitalisationThreshold: 500,
    currency: 'GBP',
    revaluationPolicy: 'None',
    active: true,
  },
  {
    id: 'proj-002',
    name: 'Furniture & Fixtures',
    description: 'Office furniture depreciation',
    method: 'Straight Line',
    usefulLife: 10,
    residualPolicy: 'percentage',
    residualPercentage: 10,
    proRataRule: 'Full Month',
    startTrigger: 'Month-End',
    capitalisationThreshold: 250,
    currency: 'GBP',
    active: true,
  },
  {
    id: 'proj-003',
    name: 'Vehicles',
    description: 'Company vehicles',
    method: 'Reducing Balance',
    usefulLife: 5,
    residualPolicy: 'value',
    residualValue: 5000,
    proRataRule: 'Actual Days',
    startTrigger: 'In-Service Date',
    capitalisationThreshold: 10000,
    currency: 'GBP',
    active: true,
  },
];

export const mockCurrentPeriod: DepreciationPeriod = {
  period: 'Period 1 2026',
  status: 'Not Started',
  expectedCompletionBy: '2026-01-31T17:00:00Z',
  assetsInScope: 247,
  priorPeriodDepreciation: 45230.50,
  exceptionsLastRun: 3,
};

export const mockDepreciationItems: DepreciationItem[] = [
  {
    id: 'dep-item-001',
    assetId: '1',
    assetCode: 'AST-2024-001',
    assetDescription: 'Dell Latitude 7420 Laptop',
    category: 'IT Equipment',
    location: 'London Office - Floor 3',
    openingNBV: 937.50,
    acquisitionCost: 1250.00,
    accumulatedDepreciation: 312.50,
    method: 'Straight Line',
    usefulLife: 4,
    lifeRemaining: 3.25,
    residualValue: 100,
    aiDepreciationPercent: 2.08,
    aiDepreciationValue: 19.53,
    aiClosingNBV: 917.97,
    confidence: 'High',
    depreciationValue: 19.53,
    closingNBV: 917.97,
    isManualOverride: false,
    validationStatus: 'valid',
    calculationInputs: {
      method: 'Straight Line',
      projectMethod: 'Straight Line',
      usefulLife: 4,
      residualValue: 100,
      postingFrequency: 'Monthly',
      baseValueOn: 'Original Postings',
      totalPostings: 48,
      postingsMade: 9,
      proRataConvention: 'Mid-Month',
      startDateRule: 'In-Service Date',
      inServiceDate: '2023-06-15',
      periodFraction: 1,
      formula: '(Total Asset Value - Residual Value) / Total Postings',
      formulaBreakdown: 'Depreciation % = 100 / Useful Life\n= 100 / 4 years\n= 25.00%\n\n= (£1,250.00 - £100.00) / 48\n= £1,150.00 / 48\n= £23.96',
    },
    priorPeriods: [
      { period: 'Feb 2024', openingNBV: 957.03, depreciation: 19.53, closingNBV: 937.50 },
      { period: 'Jan 2024', openingNBV: 976.56, depreciation: 19.53, closingNBV: 957.03 },
      { period: 'Dec 2023', openingNBV: 996.09, depreciation: 19.53, closingNBV: 976.56 },
    ],
  },
  {
    id: 'dep-item-002',
    assetId: '2',
    assetCode: 'AST-2024-002',
    assetDescription: 'Herman Miller Aeron Chair',
    category: 'Furniture',
    location: 'Manchester Office - Floor 2',
    openingNBV: 671.25,
    acquisitionCost: 895.00,
    accumulatedDepreciation: 223.75,
    method: 'Straight Line',
    usefulLife: 10,
    lifeRemaining: 8.75,
    residualValue: 89.50,
    aiDepreciationPercent: 0.75,
    aiDepreciationValue: 5.03,
    aiClosingNBV: 666.22,
    confidence: 'High',
    depreciationValue: 5.03,
    closingNBV: 666.22,
    isManualOverride: false,
    validationStatus: 'valid',
    calculationInputs: {
      method: 'Straight Line',
      projectMethod: 'Straight Line',
      usefulLife: 10,
      residualValue: 89.50,
      residualPercentage: 10,
      postingFrequency: 'Monthly',
      baseValueOn: 'Original Postings',
      totalPostings: 120,
      postingsMade: 15,
      proRataConvention: 'Full Month',
      startDateRule: 'Month-End',
      inServiceDate: '2023-03-10',
      periodFraction: 1,
      formula: '(Total Asset Value - Residual Value) / Total Postings',
      formulaBreakdown: 'Depreciation % = 100 / Useful Life\n= 100 / 10 years\n= 10.00%\n\n= (£895.00 - £89.50) / 120\n= £805.50 / 120\n= £6.71',
    },
    priorPeriods: [
      { period: 'Feb 2024', openingNBV: 676.28, depreciation: 5.03, closingNBV: 671.25 },
      { period: 'Jan 2024', openingNBV: 681.31, depreciation: 5.03, closingNBV: 676.28 },
      { period: 'Dec 2023', openingNBV: 686.34, depreciation: 5.03, closingNBV: 681.31 },
    ],
  },
  {
    id: 'dep-item-003',
    assetId: '3',
    assetCode: 'AST-2024-003',
    assetDescription: 'Ford Transit Van',
    category: 'Vehicles',
    location: 'Birmingham Depot',
    openingNBV: 19950.00,
    acquisitionCost: 28500.00,
    accumulatedDepreciation: 8550.00,
    method: 'Reducing Balance',
    usefulLife: 5,
    lifeRemaining: 3.5,
    residualValue: 5000,
    aiDepreciationPercent: 2.5,
    aiDepreciationValue: 498.75,
    aiClosingNBV: 19451.25,
    confidence: 'High',
    depreciationValue: 498.75,
    closingNBV: 19451.25,
    isManualOverride: false,
    validationStatus: 'valid',
    calculationInputs: {
      method: 'Reducing Balance',
      projectMethod: 'Reducing Balance',
      usefulLife: 5,
      residualValue: 5000,
      depreciationPercentage: 30,
      postingFrequency: 'Monthly',
      startOfYearNBV: 19950.00,
      proRataConvention: 'Actual Days',
      startDateRule: 'In-Service Date',
      inServiceDate: '2022-09-01',
      periodFraction: 1,
      formula: '((Start of Year NBV - Residual Value) x (Percentage / 100)) / Periods per Year',
      formulaBreakdown: 'Depreciation % = 30.00% (user-defined)\n\n= ((£19,950.00 - £5,000.00) x (30.00% / 100)) / 12\n= (£14,950.00 x 0.3000) / 12\n= £4,485.00 / 12\n= £373.75',
    },
    priorPeriods: [
      { period: 'Feb 2024', openingNBV: 20448.75, depreciation: 498.75, closingNBV: 19950.00 },
      { period: 'Jan 2024', openingNBV: 20947.50, depreciation: 498.75, closingNBV: 20448.75 },
      { period: 'Dec 2023', openingNBV: 21446.25, depreciation: 498.75, closingNBV: 20947.50 },
    ],
  },
];

export const mockBatch: DepreciationBatch = {
  id: 'batch-001',
  batchReference: 'DEP-2024-003',
  dateOfRun: '2024-03-15',
  period: 3,
  year: 2024,
  projectId: 'proj-001',
  projectName: 'IT Equipment Standard',
  status: 'In Progress',
  notes: '',
  items: mockDepreciationItems,
  createdBy: 'Sarah Johnson',
  createdDate: '2024-03-15T10:00:00Z',
  preparedBy: 'Sarah Johnson',
  preparedDate: '2024-03-15T10:30:00Z',
  currentStep: 'Prepare',
  totalDepreciation: 523.31,
  totalOpeningNBV: 21558.75,
  totalClosingNBV: 21035.44,
  currency: 'GBP',
};

export const mockCompletedBatches: DepreciationBatch[] = [
  {
    id: 'batch-002',
    batchReference: 'DEP-2024-002',
    auditNumber: 'AUD-2024-2002',
    dateOfRun: '2024-02-15',
    period: 2,
    year: 2024,
    projectId: 'proj-001',
    projectName: 'IT Equipment Standard',
    status: 'Completed',
    items: [],
    createdBy: 'Sarah Johnson',
    createdDate: '2024-02-15T09:00:00Z',
    preparedBy: 'Sarah Johnson',
    preparedDate: '2024-02-15T10:00:00Z',
    validatedBy: 'Michael Chen',
    validatedDate: '2024-02-15T11:30:00Z',
    reviewedBy: 'Michael Chen',
    reviewedDate: '2024-02-15T14:00:00Z',
    approvedBy: 'David Williams',
    approvedDate: '2024-02-15T15:30:00Z',
    postedBy: 'Sarah Johnson',
    postedDate: '2024-02-15T16:00:00Z',
    currentStep: 'Post',
    totalDepreciation: 45230.50,
    totalOpeningNBV: 1250000.00,
    totalClosingNBV: 1204769.50,
    currency: 'GBP',
  },
  {
    id: 'batch-003',
    batchReference: 'DEP-2024-001',
    auditNumber: 'AUD-2024-2001',
    dateOfRun: '2024-01-15',
    period: 1,
    year: 2024,
    projectId: 'proj-001',
    createdBy: 'Sarah Johnson',
    createdDate: '2024-01-15T09:00:00Z',
    projectName: 'IT Equipment Standard',
    status: 'Completed',
    items: [],
    preparedBy: 'Sarah Johnson',
    preparedDate: '2024-01-15T10:00:00Z',
    validatedBy: 'Michael Chen',
    validatedDate: '2024-01-15T11:30:00Z',
    reviewedBy: 'Michael Chen',
    reviewedDate: '2024-01-15T14:00:00Z',
    approvedBy: 'David Williams',
    approvedDate: '2024-01-15T15:30:00Z',
    postedBy: 'Sarah Johnson',
    postedDate: '2024-01-15T16:00:00Z',
    currentStep: 'Post',
    totalDepreciation: 45230.50,
    totalOpeningNBV: 1295230.50,
    totalClosingNBV: 1250000.00,
    currency: 'GBP',
  },
];
